import { useEffect, useState } from "react";
import { imageUrl } from "../../utils/imageUrl";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BsHandbag, BsStarFill, BsStarHalf, BsStar } from "react-icons/bs";
import { tomanParts, toPersianNumber } from "../../utils/persian";
import { productUrl } from "../../utils/productUrl";
import { trackAnalytics } from "../../utils/analytics";

const getProductImage = (product) =>
  product?.image1 || product?.main_image || product?.image || product?.thumbnail || product?.gallery?.[0] || "";

const getRating = (product) => {
  const value = Number(
    product?.average_rating ||
    product?.rating ||
    product?.reviews_avg_rating ||
    product?.rate ||
    0
  );
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(5, Math.max(0, value));
};

const ProductRating = ({ rating }) => {
  const rounded = Math.round(rating * 2) / 2;
  return (
    <div className="product-card-rating" aria-label={`امتیاز ${rating} از ۵`}>
      {[1,2,3,4,5].map(item => {
        if (rounded >= item) return <BsStarFill key={item} size={12} />;
        if (rounded >= item-0.5) return <BsStarHalf key={item} size={12} />;
        return <BsStar key={item} size={12} />;
      })}
      <span>{toPersianNumber(rating.toFixed(1))}</span>
    </div>
  );
};

const getTotalStock = (product) => {
  if (Array.isArray(product?.variants) && product.variants.length) {
    return product.variants.reduce((sum, variant) => {
      if (variant?.is_active === false) return sum;
      return sum + Number(variant?.stock || 0);
    }, 0);
  }
  return Number(product?.stock || product?.available_stock || 0);
};

let specialSaleCache = null;
let specialSalePromise = null;

const loadSpecialSaleProducts = () => {
  if (specialSaleCache) return Promise.resolve(specialSaleCache);

  if (!specialSalePromise) {
    specialSalePromise = fetch("/api/special-sale/current")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const map = new Map();

        if (data?.campaign && Array.isArray(data.products)) {
          data.products.forEach((item) => {
            const id = String(item.id || item._id || "");
            if (id) map.set(id, item);
          });
        }

        specialSaleCache = map;
        return map;
      })
      .catch(() => {
        specialSaleCache = new Map();
        return specialSaleCache;
      });
  }

  return specialSalePromise;
};

const mergeSpecialSaleProduct = (product, saleItem) => {
  if (!product || !saleItem) return product;

  const basePrice = Number(product.price || saleItem.price || 0);
  const campaignPrice = Number(saleItem.campaign_sale_price || saleItem.sale_price || 0);

  if (!campaignPrice || !basePrice || campaignPrice >= basePrice) {
    return product;
  }

  const discount = Math.round((1 - campaignPrice / basePrice) * 100);

  return {
    ...product,
    campaign_sale_price: campaignPrice,
    sale_price: campaignPrice,
    discount,
    discount_percent: discount,
    is_campaign_sale: true,
    special_sale_campaign: saleItem.special_sale_campaign || null
  };
};

// ---------- START: NEW LOGIC FOR "NEW" TAG ----------
const isNewProduct = (product, days = 7) => {
  if (product?.is_new === true || product?.isNew === true) return true;

  const dateValue =
    product?.bumped_at ||
    product?.bumpedAt ||
    product?.created_at ||
    product?.createdAt ||
    product?.created ||
    product?.published_at ||
    null;

  if (!dateValue) return false;

  const productDate = new Date(dateValue);
  if (Number.isNaN(productDate.getTime())) return false;

  const diffDays = (Date.now() - productDate.getTime()) / (1000 * 60 * 60 * 24);

  return diffDays >= 0 && diffDays <= days;
};
// ---------- END: NEW LOGIC ----------

const ProductCard = ({ product, index=0, layout="grid", compact=false, priority=false }) => {
  const [campaignItem, setCampaignItem] = useState(null);
  const productKey = String(product?.id || product?._id || "");

  useEffect(() => {
    let cancelled = false;

    if (!productKey || product?.campaign_sale_price) {
      setCampaignItem(null);
      return;
    }

    loadSpecialSaleProducts().then((map) => {
      if (!cancelled) {
        setCampaignItem(map.get(productKey) || null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [productKey, product?.campaign_sale_price]);

  const displayProduct = mergeSpecialSaleProduct(product, campaignItem);
  const basePrice = Number(displayProduct.price || 0);
  const campaignSalePrice = displayProduct.campaign_sale_price ? Number(displayProduct.campaign_sale_price) : 0;
  const normalSalePrice = displayProduct.sale_price ? Number(displayProduct.sale_price) : 0;
  const finalPrice = campaignSalePrice || normalSalePrice || basePrice;
  const hasDiscount = basePrice > 0 && finalPrice > 0 && finalPrice < basePrice;
  const calculatedDiscount = hasDiscount ? Math.round((1 - finalPrice / basePrice) * 100) : 0;
  const discount = Number(displayProduct.discount || displayProduct.discount_percent || calculatedDiscount || 0);
  const image = getProductImage(displayProduct);

  const finalPriceParts = tomanParts(finalPrice);
  const basePriceParts = tomanParts(basePrice);
  const title = displayProduct.title || displayProduct.name || "محصول جوتی";
  const productImageAlt = title.includes("جوتی") ? title : `${title} | خرید از جوتی`;
  const isNew = isNewProduct(displayProduct, 7); // <-- NEW LOGIC
  const isCampaignSale = Boolean(campaignSalePrice);
  const totalStock = getTotalStock(displayProduct);
  const isUnavailable = totalStock <= 0;
  const rating = getRating(displayProduct);

  return (
    <motion.div
      initial={{ opacity:0, y:18 }}
      whileInView={{ opacity:1, y:0 }}
      viewport={{ once:true, margin:"-40px" }}
      transition={{ duration:0.34, delay: Math.min(index*0.035,0.16) }}
      className={`product-card-wrap ${compact ? "product-card-wrap-compact" : ""} ${isUnavailable ? "product-card-wrap-unavailable" : ""}`}
    >
      <Link
        to={productUrl(displayProduct)}
        onClick={() => trackAnalytics("product_click", {
          product_id: displayProduct.id || displayProduct._id || null,
          category_id: displayProduct.category_id || null,
          metadata: { product_name: title, layout, compact }
        })}
        className={`product-card ${compact ? "product-card-compact" : ""} ${isCampaignSale ? "product-card-campaign-sale" : ""} ${isUnavailable ? "product-card-unavailable" : ""}`}
      >
        <div className="product-image-box">
          <img src={imageUrl(image)} alt={productImageAlt} className="product-image" loading={priority ? "eager":"lazy"} fetchPriority={priority ? "high":"auto"} decoding="async" />
          <div className="product-card-badges">
            {isUnavailable ? (
              <span className="product-unavailable-badge">ناموجود</span>
            ) : (
              <>
                {isNew && <span className="product-soft-badge product-new-badge"><b>جدید</b></span>}
              </>
            )}
          </div>

          {!isUnavailable && isCampaignSale && (
            <div className="product-campaign-ribbon">فروش ویژه</div>
          )}

          <div className="product-card-action">
            <BsHandbag size={16} />
            <span>{isUnavailable ? "مشاهده جزئیات" : "مشاهده محصول"}</span>
          </div>
        </div>

        <div className="product-info">
          <h3>{title}</h3>
          {rating>0 && <ProductRating rating={rating} />}
          {!isUnavailable && hasDiscount && (
            <span className={isCampaignSale ? "product-discount-corner product-discount-large" : "product-discount-corner"}>
              {toPersianNumber(discount)}٪
            </span>
          )}

          <div className="product-price-row">
            {isUnavailable ? (
              <strong className="product-unavailable-price">ناموجود</strong>
            ) : (
              <div className="product-price-stack">
                {isCampaignSale && <span className="campaign-price-label">قیمت کمپین</span>}
                <div className="product-modern-price">
                  <strong>{finalPriceParts.amount}</strong>
                  <span>{finalPriceParts.currency}</span>
                </div>
                <div className="product-old-price-slot">
                  {hasDiscount && (
                    <del className="product-old-price">{basePriceParts.amount}</del>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
